import * as Sentry from '@sentry/nextjs';
import { ensureClient, slugify } from './util';
import type { Tables, Database, TablesInsert } from '@/types/supabase';
import type { AdminMatchRatingsTableRow } from '@/types/admin';
import { getSupabaseAdminClient } from '@/lib/supabase/admin';
import { playerModelV2 } from './playerModelV2';
import { calculateEloDelta, type GameScore } from './ratingUtil';
import { isCompletedGame, retirementRatingGames } from '@/app/[locale]/match/libs/retirement';
import type { MatchWithRating, MatchWithRatingsV2 } from '@/components/match/types';
import { adminUpdate, get as getPlayer } from './playerModel';
import { ratingRecalcDueModel } from './ratingRecalcDueModel';

type Rating = Tables<'rating'>;
type Match = Tables<'match'>;
type MatchGame = Tables<'match_game'>;
type MatchWithGames = Match & { match_game: MatchGame[] };
type RatingWithMatch = Rating & { match: MatchWithGames };
type RatingInsert = Database['public']['Tables']['rating']['Insert'];

export type PlayerLite = Pick<
  Tables<'player'>,
  'id' | 'first_name' | 'last_name' | 'username' | 'current_rating' | 'profile_photo'
>;
type PlayerWithNationality = PlayerLite & { nationality?: string | null };
type GameScoreLite = {
  game_number: number | null;
  player_1_score: number | null;
  player_2_score: number | null;
  match_game_points?: Array<{ id: string }>;
  /** `'in_progress'` marks a retired match's unfinished game. */
  status?: string | null;
};

type MatchLite = Pick<
  Tables<'match'>,
  | 'id'
  | 'played_at'
  | 'player_1_id'
  | 'player_2_id'
  | 'player_1_games'
  | 'player_2_games'
  | 'winner_id'
  | 'status'
> & {
  best_of?: number | null;
  points_per_game?: number | null;
  retired_player_id?: string | null;
  /** Embedded schedule rows; non-empty means Mission Control can reach this match. */
  schedules?: Array<{ id: string }> | null;
  match_games?: GameScoreLite[];
};

// Type for rating rows from database queries
type RatingRow = {
  id: string;
  rated_at: string | null;
  event_type: string | null;
  rating_before: number | null;
  rating_after: number | null;
  opponent_rating_before: number | null;
  opponent_rating_after: number | null;
  k_factor: number | null;
  player_id: string;
  match: MatchLite | null;
  opponent: PlayerLite | null;
  player: PlayerLite | null;
};

// Type for enriched rating data with additional fields
type EnrichedRatingRow = RatingRow & {
  event_id?: string | null;
  event_name?: string | null;
  event_division_name?: string | null;
  event_location_name?: string | null;
};

// Type for match data with player information
type MatchWithPlayers = {
  id: string;
  played_at: string | null;
  player_1_id: string;
  player_2_id: string;
  player_1_games: number | null;
  player_2_games: number | null;
  winner_id: string | null;
  status: string;
  best_of: number | null;
  points_per_game: number | null;
  player1: PlayerWithNationality | null;
  player2: PlayerWithNationality | null;
};

// Type for rating data from database
type RatingData = {
  id: string;
  rating_after: number | null;
  rated_at: string | null;
};

// Type for database rating query result
type DatabaseRatingRow = {
  id: string;
  rated_at: string | null;
  event_type: string | null;
  rating_before: number | null;
  rating_after: number | null;
  opponent_rating_before: number | null;
  opponent_rating_after: number | null;
  k_factor: number | null;
  player_id: string;
  match: MatchLite | null;
  opponent: PlayerLite | null;
  player:
    | (PlayerLite & {
        federation_user?: Array<{ federation_id: string; status: string }>;
      })
    | null;
};

function mapToAdminRatingRow(r: DatabaseRatingRow): AdminMatchRatingsTableRow {
  const match = r.match ?? null;
  const opponent = r.opponent ?? null;
  const player = r.player ?? null;
  return {
    id: String(r.id),
    rated_at: r.rated_at ?? match?.played_at ?? null,
    event_type: r.event_type ?? null,
    rating_before: r.rating_before ?? null,
    rating_after: r.rating_after ?? null,
    opponent_rating_before: r.opponent_rating_before ?? null,
    opponent_rating_after: r.opponent_rating_after ?? null,
    k_factor: r.k_factor ?? null,
    match: match
      ? {
          id: String(match.id),
          played_at: match.played_at ?? null,
          player_1_id: String(match.player_1_id),
          player_2_id: String(match.player_2_id),
          player_1_games: match.player_1_games ?? null,
          player_2_games: match.player_2_games ?? null,
          winner_id: match.winner_id ?? null,
          best_of: match.best_of ?? null,
          points_per_game: match.points_per_game ?? null,
          retired_player_id: match.retired_player_id ?? null,
          in_mission_control: (match.schedules?.length ?? 0) > 0,
          match_games: match.match_games ?? [],
        }
      : null,
    opponent: opponent
      ? {
          id: opponent.id,
          first_name: opponent.first_name ?? null,
          last_name: opponent.last_name ?? null,
          username: opponent.username ?? null,
          current_rating: opponent.current_rating ?? null,
          profile_photo: opponent.profile_photo ?? null,
        }
      : null,
    player: player
      ? {
          id: player.id,
          first_name: player.first_name ?? null,
          last_name: player.last_name ?? null,
          username: player.username ?? null,
          current_rating: player.current_rating ?? null,
          profile_photo: player.profile_photo ?? null,
        }
      : null,
  };
}

const ratingModel = {
  /**
   * Get rating history rows in AdminMatchRatingsTableRow shape.
   * - If playerId provided: returns that player's rating history (joins match and opponent)
   * - Else: returns federation-wide recent match rows mapped into same shape, using
   *         the rating_after values for both participants when available.
   */
  async getAdminRatings(params: {
    federationId: string;
    page?: number;
    pageSize?: number;
    search?: string;
    playerId?: string;
  }): Promise<{ rows: AdminMatchRatingsTableRow[]; total?: number }> {
    const client = ensureClient();
    const federationId = String(params.federationId || '');
    const page = Math.max(1, Number(params.page || 1));
    const pageSize = Math.min(100, Math.max(1, Number(params.pageSize || 20)));
    const search = String(params.search || '').toLowerCase();
    const playerId = String(params.playerId || '');
    if (!federationId) return { rows: [], total: 0 };

    let allRows: DatabaseRatingRow[] = [];

    // If a playerId is provided: return that player's rating history directly
    if (playerId) {
      const { data: ratingRows, error: ratingErr } = await client
        .from('rating')
        .select(
          `
            id,
            rated_at,
            event_type,
            rating_before,
            rating_after,
            opponent_rating_before,
            opponent_rating_after,
            k_factor,
            player_id,
            match:match_id(
              id,
              played_at,
              player_1_id,
              player_2_id,
              player_1_games,
              player_2_games,
              winner_id,
              best_of,
              points_per_game,
              status,
              retired_player_id,
              schedules:event_match_schedule(id),
              match_games:match_game(
                game_number,
                player_1_score,
                player_2_score,
                status
              )
            ),
            opponent:player!rating_opponent_id_fkey(
              id,
              first_name,
              last_name,
              username,
              current_rating,
              profile_photo
            ),
            player:player!rating_player_id_fkey(
              id,
              first_name,
              last_name,
              username,
              current_rating,
              profile_photo
            )
          `,
        )
        .eq('player_id', playerId)
        .order('rated_at', { ascending: false });
      if (ratingErr) throw ratingErr;
      allRows = (ratingRows ?? []) as unknown as DatabaseRatingRow[];
    } else {
      // Otherwise: federation-wide rating history across all event types (including adjustments)
      // Query 1: Get match-based ratings (one per match, preferring player_1's perspective)
      // Only include completed matches
      // Use inner join on player->federation_user, then filter in application code
      const { data: allMatchRatings, error: matchErr } = await client
        .from('rating')
        .select(
          `
            id,
            rated_at,
            event_type,
            rating_before,
            rating_after,
            opponent_rating_before,
            opponent_rating_after,
            k_factor,
            player_id,
            match:match_id (
              id,
              played_at,
              player_1_id,
              player_2_id,
              player_1_games,
              player_2_games,
              winner_id,
              best_of,
              points_per_game,
              status
            ),
            opponent:player!rating_opponent_id_fkey (
              id,
              first_name,
              last_name,
              username,
              current_rating,
              profile_photo
            ),
            player:player!rating_player_id_fkey!inner (
              id,
              first_name,
              last_name,
              username,
              current_rating,
              profile_photo,
              federation_user!inner (
                federation_id,
                status
              )
            )
          `,
        )
        .not('match_id', 'is', null)
        .eq('match.status', 'completed')
        .order('rated_at', { ascending: false });
      if (matchErr) throw matchErr;

      // Filter by federation in application code (PostgREST limitation)
      const matchRatings = (allMatchRatings ?? []).filter((rating: DatabaseRatingRow) => {
        const federationUser = rating.player?.federation_user?.[0];
        return (
          federationUser?.federation_id === federationId && federationUser?.status === 'active'
        );
      });

      // Query 2: Get adjustment ratings (no match_id)
      const { data: allAdjustmentRatings, error: adjustErr } = await client
        .from('rating')
        .select(
          `
            id,
            rated_at,
            event_type,
            rating_before,
            rating_after,
            opponent_rating_before,
            opponent_rating_after,
            k_factor,
            player_id,
            match:match_id(
              id,
              played_at,
              player_1_id,
              player_2_id,
              player_1_games,
              player_2_games,
              winner_id,
              best_of,
              points_per_game,
              status
            ),
            opponent:player!rating_opponent_id_fkey(
              id,
              first_name,
              last_name,
              username,
              current_rating,
              profile_photo
            ),
            player:player!rating_player_id_fkey!inner (
              id,
              first_name,
              last_name,
              username,
              current_rating,
              profile_photo,
              federation_user!inner (
                federation_id,
                status
              )
            )
          `,
        )
        .is('match_id', null)
        .order('rated_at', { ascending: false });
      if (adjustErr) throw adjustErr;

      // Filter by federation in application code (PostgREST limitation)
      const adjustmentRatings = (allAdjustmentRatings ?? []).filter((rating: DatabaseRatingRow) => {
        const federationUser = rating.player?.federation_user?.[0];
        return (
          federationUser?.federation_id === federationId && federationUser?.status === 'active'
        );
      });

      // Group match ratings by match_id, preferring player_1's record for consistent display
      const matchGroups = new Map<string, DatabaseRatingRow>();
      (matchRatings ?? []).forEach((rating: DatabaseRatingRow) => {
        const matchId = rating.match?.id;
        if (matchId) {
          const existing = matchGroups.get(matchId);
          if (
            !existing ||
            (rating.player_id === rating.match?.player_1_id &&
              existing.player_id !== existing.match?.player_1_id)
          ) {
            matchGroups.set(matchId, rating);
          }
        }
      });

      // Combine match ratings and adjustments
      allRows = [
        ...Array.from(matchGroups.values()),
        ...(adjustmentRatings ?? []).map((r) => r as DatabaseRatingRow),
      ];

      // Sort by date descending
      allRows.sort((a, b) => {
        const aTime = a.rated_at || a.match?.played_at || '';
        const bTime = b.rated_at || b.match?.played_at || '';
        return bTime.localeCompare(aTime);
      });
    }

    let filtered = allRows;
    if (search) {
      filtered = allRows.filter((r) => {
        const player = r.player;
        const playerName = player
          ? playerModelV2.formatName(player.first_name, player.last_name).toLowerCase()
          : '';
        const opponentName = r.opponent
          ? playerModelV2.formatName(r.opponent.first_name, r.opponent.last_name).toLowerCase()
          : '';
        return playerName.includes(search) || opponentName.includes(search);
      });
    }

    const total = filtered.length;
    const start = (page - 1) * pageSize;
    const pageItems = filtered.slice(start, start + pageSize);

    const rows: AdminMatchRatingsTableRow[] = pageItems.map(mapToAdminRatingRow);

    return { rows, total };
  },

  async getLatestRatingForPlayer(playerId: string): Promise<number | null> {
    const client = ensureClient();
    if (!playerId) return null;
    const { data, error } = await client
      .from('rating')
      .select('rating_after, rated_at')
      .eq('player_id', playerId)
      // A null K-factor is a match recorded for history only (its event had
      // ratings off): a snapshot, never the player's rating.
      .not('k_factor', 'is', null)
      .order('rated_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    const val = (data as RatingData | null)?.rating_after ?? null;
    return val;
  },

  /**
   * Get match history for a specific player by slug or player ID.
   * Returns formatted match data suitable for display in MatchListGrid component.
   */
  async getMatchHistoryForPlayer(params: {
    slug?: string;
    playerId?: string;
    isJapanese?: boolean;
  }): Promise<MatchWithRating[]> {
    const { slug, playerId, isJapanese } = params;

    // Resolve player ID from slug if not provided
    const targetPlayerId = await this.resolvePlayerId({ slug, playerId });
    if (!targetPlayerId) return [];

    // Get all rating data with a single comprehensive query
    const ratingData = await this.getRatingHistoryWithEnrichment(targetPlayerId);

    // Transform to the expected format
    const matches = this.transformRatingDataToMatches(ratingData, targetPlayerId, isJapanese);

    // Suppress ratings for events under federations with ratings disabled
    return this.suppressRatingsForDisabledFederations(matches);
  },

  /**
   * Resolve player ID from slug or use provided playerId
   */
  async resolvePlayerId(params: { slug?: string; playerId?: string }): Promise<string | undefined> {
    const { slug, playerId } = params;
    if (playerId) return playerId;
    if (!slug) return undefined;

    const client = ensureClient();

    // Try username first
    const { data: byUsername, error: byUsernameErr } = await client
      .from('player')
      .select('id')
      .eq('username', slug)
      .maybeSingle();
    if (byUsernameErr) throw byUsernameErr;
    if (byUsername?.id) return byUsername.id as string;

    // Fallback: compute legacy slug from first.last
    const { data: players, error: playersErr } = await client
      .from('player')
      .select('id, first_name, last_name')
      .limit(1000);
    if (playersErr) throw playersErr;

    const found = (players || []).find(
      (p: Pick<Tables<'player'>, 'first_name' | 'last_name'>) =>
        slugify(`${p.first_name}.${p.last_name}`) === slug,
    );
    return found?.id || undefined;
  },

  /**
   * Get rating history with all related data in a single query
   */
  async getRatingHistoryWithEnrichment(playerId: string): Promise<EnrichedRatingRow[]> {
    const client = ensureClient();

    // Get all rating data - filtering for completed matches will be done in transformRatingDataToMatches
    const { data, error } = await client
      .from('rating')
      .select(
        `
        id,
        rated_at,
        event_type,
        rating_before,
        rating_after,
        opponent_rating_before,
        opponent_rating_after,
        k_factor,
        event_id,
        event_name,
        event_division_name,
        event_location_name,
        match:match_id(
          id,
          played_at, 
          player_1_id, 
          player_2_id, 
          player_1_games, 
          player_2_games, 
          winner_id,
          best_of,
          points_per_game,
          status,
          retired_player_id,
          match_games:match_game(
            game_number,
            player_1_score,
            player_2_score,
            status,
            match_game_points:match_game_point(
              id
            )
          )
        ),
        opponent:player!rating_opponent_id_fkey(
          id, 
          first_name, 
          last_name, 
          nationality, 
          username,
          current_rating,
          profile_photo
        )
      `,
      )
      .eq('player_id', playerId)
      .order('rated_at', { ascending: false });

    if (error) throw error;
    return (data || []) as unknown as EnrichedRatingRow[];
  },

  /**
   * Transform raw rating data to MatchWithRating format
   * Filters out incomplete matches to ensure only completed matches are included
   */
  transformRatingDataToMatches(
    ratingData: EnrichedRatingRow[],
    targetPlayerId: string,
    isJapanese?: boolean,
  ): MatchWithRating[] {
    // Filter out incomplete matches (adjustment records are always included)
    const filteredData = ratingData.filter(
      (r) => r.event_type === 'adjustment' || r.match?.status === 'completed',
    );

    return filteredData.map((r) => {
      const match = r.match;
      const opponent = r.opponent;

      const dateISO = r.rated_at
        ? new Date(r.rated_at).toISOString()
        : match?.played_at
          ? new Date(match.played_at).toISOString()
          : new Date().toISOString();

      const oppName = opponent
        ? playerModelV2.formatName(opponent.first_name, opponent.last_name, null, {
            isJapanese: !!isJapanese,
          })
        : 'Unknown';
      const opponentUsername = opponent?.username || '';

      const result = this.calculateMatchResult(match, targetPlayerId);
      const venue = r.event_location_name || '—';

      // Get match format from database or use default
      let system = 'Par 11, Best of 5'; // default
      if (match) {
        const bestOf = match.best_of || 5;
        const pointsPerGame = match.points_per_game || 11;
        system = `Par ${pointsPerGame}, Best of ${bestOf}`;
      }

      // A null K-factor is a match recorded for history only (its event had
      // ratings off). The row itself says so — hiding its numbers must not
      // depend on the event lookup in suppressRatingsForDisabledFederations.
      const historyOnly = r.k_factor === null;
      const myRating = !historyOnly && r.rating_after != null ? Number(r.rating_after) : null;
      const myDelta =
        !historyOnly && r.rating_after != null && r.rating_before != null
          ? Number(r.rating_after) - Number(r.rating_before)
          : null;
      const oppRating =
        !historyOnly && r.opponent_rating_after != null ? Number(r.opponent_rating_after) : null;
      const oppDelta =
        !historyOnly && r.opponent_rating_after != null && r.opponent_rating_before != null
          ? Number(r.opponent_rating_after) - Number(r.opponent_rating_before)
          : null;
      const opponentCountry = (opponent as PlayerWithNationality)?.nationality || null;

      const isAdjustment = !match || r.event_type === 'adjustment';

      // Extract and format game scores from the perspective of the target player
      const isPlayer1 = match?.player_1_id === targetPlayerId;
      const gameScores = match?.match_games
        ?.sort((a, b) => (a.game_number || 0) - (b.game_number || 0))
        .map((game) => {
          const p1Score = game.player_1_score || 0;
          const p2Score = game.player_2_score || 0;

          // Show from the perspective of the target player
          const status = game.status === 'in_progress' ? { status: game.status } : {};
          if (isPlayer1) {
            return {
              player1: p1Score,
              player2: p2Score,
              ...status,
            };
          } else {
            return {
              player1: p2Score,
              player2: p1Score,
              ...status,
            };
          }
        })
        .filter((game) => game.player1 > 0 || game.player2 > 0);

      // Check if there are match_game_point records to determine if scorecard is available
      const hasScorecard =
        !isAdjustment &&
        match?.match_games?.some(
          (game) => game.match_game_points && game.match_game_points.length > 0,
        );

      return {
        id: match?.id || '',
        dateISO,
        opponent: oppName,
        opponentUsername,
        opponentProfilePhoto: opponent?.profile_photo || null,
        system,
        result: isAdjustment ? '-' : result,
        venue: isAdjustment ? '—' : venue,
        competition: isAdjustment ? r.event_name || 'Ranking Adjustment' : r.event_name || null,
        competitionDivision: r.event_division_name || null,
        winnerId: r.match?.winner_id || null,
        ...(match?.retired_player_id ? { retiredPlayerId: match.retired_player_id } : {}),
        myRating,
        myDelta,
        oppRating,
        oppDelta,
        opponentCountry,
        isAdjustment,
        eventId: r.event_id || null,
        gameScores: gameScores && gameScores.length > 0 ? gameScores : undefined,
        hasScorecard,
        matchStatus: match?.status || 'completed', // Add match status
        appState: null, // No app_state in rating history
      };
    });
  },

  /**
   * Calculate match result from the perspective of the target player
   */
  calculateMatchResult(match: MatchLite | null, targetPlayerId: string): string {
    if (!match) return '-';

    const p1 = match.player_1_games ?? null;
    const p2 = match.player_2_games ?? null;

    if (p1 === null || p2 === null) return '-';

    // Show from the perspective of the target player
    const isPlayer1 = match.player_1_id === targetPlayerId;
    if (isPlayer1) {
      return `${p1}-${p2}`;
    } else {
      return `${p2}-${p1}`;
    }
  },

  /**
   * Get upcoming matches for a specific player by slug or player ID.
   * Returns matches that are scheduled but not yet started.
   */
  async getUpcomingMatches(params: {
    slug?: string;
    playerId?: string;
    isJapanese?: boolean;
  }): Promise<MatchWithRating[]> {
    const client = ensureClient();
    const { slug, playerId, isJapanese } = params;

    // Resolve player ID from slug if not provided
    const targetPlayerId = await this.resolvePlayerId({ slug, playerId });
    if (!targetPlayerId) return [];

    // Get upcoming matches (not started, scheduled in the future)
    const { data, error } = await client
      .from('match')
      .select(
        `
        id,
        played_at,
        player_1_id,
        player_2_id,
        player_1_games,
        player_2_games,
        winner_id,
        status,
        best_of,
        points_per_game,
        player1:player!match_player_1_id_fkey(
          id,
          first_name,
          last_name,
          nationality,
          username,
          current_rating,
          profile_photo
        ),
        player2:player!match_player_2_id_fkey(
          id,
          first_name,
          last_name,
          nationality,
          username,
          current_rating,
          profile_photo
        )
      `,
      )
      .or(`player_1_id.eq.${targetPlayerId},player_2_id.eq.${targetPlayerId}`)
      .eq('status', 'not_started')
      .gte('played_at', new Date().toISOString())
      .order('played_at', { ascending: true });

    if (error) throw error;

    return (data || []).map((match: MatchWithPlayers) => {
      const isPlayer1 = match.player_1_id === targetPlayerId;
      const opponent = isPlayer1 ? match.player2 : match.player1;
      const opponentName = opponent
        ? playerModelV2.formatName(opponent.first_name, opponent.last_name, null, {
            isJapanese: !!isJapanese,
          })
        : 'Unknown';
      const opponentUsername = opponent?.username || '';
      return {
        id: match.id,
        dateISO: match.played_at || '',
        opponent: opponentName,
        opponentUsername,
        opponentCountry: opponent?.nationality || null,
        opponentProfilePhoto: opponent?.profile_photo || null,
        system: 'Par 11, Best of 5',
        result: '-', // Not played yet
        venue: '—',
        competition: null,
        competitionDivision: null,
        winnerId: null,
        myRating: null, // Will be calculated when match is played
        myDelta: null,
        oppRating: null,
        oppDelta: null,
        isAdjustment: false,
      };
    });
  },

  /**
   * Get in-progress matches for a specific player by slug or player ID.
   * Returns matches that are currently being played.
   */
  async getInProgressMatches(params: {
    slug?: string;
    playerId?: string;
  }): Promise<MatchWithRatingsV2[]> {
    const client = ensureClient();
    const { slug, playerId } = params;

    // Resolve player ID from slug if not provided
    const targetPlayerId = await this.resolvePlayerId({ slug, playerId });
    if (!targetPlayerId) return [];

    // Get in-progress matches with event_match_schedule information
    const { data, error } = await client
      .from('match')
      .select(
        `
        id,
        played_at,
        player_1_id,
        player_2_id,
        player_1_games,
        player_2_games,
        winner_id,
        status,
        best_of,
        points_per_game,
        player1:player!match_player_1_id_fkey(
          id,
          first_name,
          last_name,
          nationality,
          username,
          current_rating,
          profile_photo
        ),
        player2:player!match_player_2_id_fkey(
          id,
          first_name,
          last_name,
          nationality,
          username,
          current_rating,
          profile_photo
        ),
        event_match_schedule!match_id(
          id,
          event:event_id(name),
          event_group:event_group_id(name)
        )
      `,
      )
      .or(
        `player_1_id.eq.${targetPlayerId},player_2_id.eq.${targetPlayerId},scored_by_player_id.eq.${targetPlayerId}`,
      )
      .eq('status', 'in_progress')
      .order('played_at', { ascending: false });

    if (error) throw error;

    return (data || []).map(
      (
        match: MatchWithPlayers & {
          event_match_schedule?: {
            id: string;
            event?: { name: string } | null;
            event_group?: { name: string } | null;
          }[];
          scored_by_player_id?: string;
        },
      ) => {
        const isPlayer1 = match.player_1_id === targetPlayerId;
        const isPlayer2 = match.player_2_id === targetPlayerId;
        const isScoredByPlayer = match.scored_by_player_id === targetPlayerId;

        // Calculate current score (only if player is a participant)
        const result =
          isPlayer1 || isPlayer2
            ? this.calculateMatchResult(match as MatchLite, targetPlayerId)
            : 'In Progress';

        // Check if match is linked to event_match_schedule
        const schedule =
          match.event_match_schedule && match.event_match_schedule.length > 0
            ? match.event_match_schedule[0]
            : null;
        const isLinkedToEventSchedule = !!schedule;

        return {
          id: match.id,
          dateISO: match.played_at || '',
          player1: match.player1
            ? {
                id: match.player1.id,
                first_name: match.player1.first_name,
                last_name: match.player1.last_name,
                username: match.player1.username || '',
                current_rating: match.player1.current_rating,
                profile_photo: match.player1.profile_photo,
                nationality: match.player1.nationality,
              }
            : null,
          player2: match.player2
            ? {
                id: match.player2.id,
                first_name: match.player2.first_name,
                last_name: match.player2.last_name,
                username: match.player2.username || '',
                current_rating: match.player2.current_rating,
                profile_photo: match.player2.profile_photo,
                nationality: match.player2.nationality,
              }
            : null,
          system: 'Par 11, Best of 5',
          result,
          venue: '',
          competition: schedule?.event?.name || null,
          competitionDivision: schedule?.event_group?.name || null,
          winnerId: null,
          myRating: null, // Will be calculated when match is completed
          myDelta: null,
          oppRating: null,
          oppDelta: null,
          isAdjustment: false,
          isLinkedToEventSchedule,
          isMarkedByPlayer: isScoredByPlayer,
        };
      },
    );
  },

  /**
   * Upsert a rating record - create new or update existing
   */
  async upsert(
    id: string | undefined,
    data: Database['public']['Tables']['rating']['Insert'],
  ): Promise<{ ratingId: string }> {
    const admin = getSupabaseAdminClient();
    if (!admin) {
      throw new Error('Admin client not configured');
    }

    try {
      if (id) {
        // Update existing rating
        const { data: updatedRating, error: updateErr } = await admin
          .from('rating')
          .update(data as Database['public']['Tables']['rating']['Update'])
          .eq('id', id)
          .select('id')
          .maybeSingle();

        if (updateErr) {
          throw new Error(`Failed to update rating: ${updateErr.message}`);
        }

        return { ratingId: updatedRating?.id || id };
      } else {
        // Create new rating or update if conflict (e.g. match_id + player_id)
        const { data: newRating, error: createErr } = await admin
          .from('rating')
          .upsert(data)
          .select('id')
          .maybeSingle();

        if (createErr) {
          throw new Error(`Failed to create/upsert rating: ${createErr.message}`);
        }

        return { ratingId: newRating?.id || '' };
      }
    } catch (error) {
      console.error('Error upserting rating:', error);
      throw error;
    }
  },

  async getRatingWithMatch(ratingId: string): Promise<RatingWithMatch | null> {
    const admin = getSupabaseAdminClient();
    if (!admin) throw new Error('Supabase admin not configured');

    const { data, error } = await admin
      .from('rating')
      .select('*, match(*, match_game(*, match_game_point(*)))')
      .eq('id', ratingId)
      .maybeSingle();
    if (error) throw error;
    return data as unknown as RatingWithMatch | null;
  },

  // adjust rating of a player by admin
  async adminAdjustRating(playerId: string, newRating: number, adjustmentNote: string) {
    if (!playerId || !newRating || !adjustmentNote) throw new Error('Missing required parameters');

    const admin = getSupabaseAdminClient();
    if (!admin) throw new Error('Supabase admin not configured');

    const { data: latest } = await admin
      .from('rating')
      .select('id, rating_after')
      .eq('player_id', playerId)
      .not('k_factor', 'is', null)
      .order('rated_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    const before = (latest?.rating_after as number | null) ?? 0;
    const insertPayload: TablesInsert<'rating'> = {
      player_id: playerId,
      opponent_id: playerId,
      rating_before: before,
      rating_after: newRating,
      opponent_rating_before: before,
      opponent_rating_after: newRating,
      event_type: 'adjustment',
      rated_at: new Date().toISOString(),
      adjustment_note: adjustmentNote,
    };
    const { error: insErr } = await admin.from('rating').insert(insertPayload);
    if (insErr) throw insErr;

    await adminUpdate(playerId, { current_rating: newRating });
  },

  async createRatingAndCalculate(
    rating: RatingInsert,
    match: MatchWithGames,
    options: { unrated?: boolean } = {},
  ) {
    const admin = getSupabaseAdminClient();
    if (!admin) throw new Error('Supabase admin not configured');

    // Always fetch both player records so we can detect guest players;
    // ratings are not applied to or by guests.
    const [player1Rating, player2Rating, player1Data, player2Data] = await Promise.all([
      rating.rating_before !== undefined
        ? Promise.resolve(rating.rating_before)
        : this.getLatestRatingForPlayer(rating.player_id),
      rating.opponent_rating_before !== undefined
        ? Promise.resolve(rating.opponent_rating_before)
        : this.getLatestRatingForPlayer(rating.opponent_id),
      getPlayer(rating.player_id),
      getPlayer(rating.opponent_id),
    ]);

    const player1Before = player1Rating ?? player1Data?.current_rating ?? null;
    const player2Before = player2Rating ?? player2Data?.current_rating ?? null;
    const kFactor = rating.k_factor;

    const eitherIsGuest = !!(player1Data?.is_guest || player2Data?.is_guest);

    // If either player is a guest, the match doesn't affect ratings: we still
    // record rating rows (so the match shows in history) with rating_after =
    // rating_before, and skip the player.current_rating update.
    // The same goes for a match in an event with ratings off (`unrated`); its
    // rows carry a null K-factor, which is what tells recalcRatings to keep
    // them at zero change.
    if (eitherIsGuest || options.unrated) {
      // An unrated row keeps a missing rating as null rather than inventing a 0.
      const p1 = options.unrated ? player1Before : (player1Before ?? 0);
      const p2 = options.unrated ? player2Before : (player2Before ?? 0);
      if (options.unrated) rating = { ...rating, k_factor: null };
      await Promise.all([
        this.upsert(undefined, {
          ...rating,
          rating_before: p1,
          opponent_rating_before: p2,
          rating_after: p1,
          opponent_rating_after: p2,
        }),
        this.upsert(undefined, {
          ...rating,
          player_id: rating.opponent_id,
          opponent_id: rating.player_id,
          rating_before: p2,
          opponent_rating_before: p1,
          rating_after: p2,
          opponent_rating_after: p1,
        }),
      ]);
      return { player1After: p1, player2After: p2 };
    }

    // gamescores array should be rotated the right way, depending on rating.match.player_1_id and rating.match.player_2_id
    // A retirement is rated as a win for the winner (its credited games); otherwise the
    // completed games as played.
    const fromPlayer1: Array<readonly [number, number]> =
      retirementRatingGames(match) ??
      (match.match_game ?? [])
        .filter(isCompletedGame)
        .map((game) => [game.player_1_score, game.player_2_score] as const);
    const gameScores = fromPlayer1.map(
      ([p1, p2]) => (rating.player_id === match.player_1_id ? [p1, p2] : [p2, p1]) as GameScore,
    );

    const { delta1, delta2 } = calculateEloDelta(
      player1Before || 0,
      player2Before || 0,
      gameScores,
      kFactor || 30,
    );

    const player1After = Number(((player1Before || 0) + delta1).toFixed(2));
    const player2After = Number(((player2Before || 0) + delta2).toFixed(2));

    // create rating both ways and update player ratings in parallel
    await Promise.all([
      this.upsert(undefined, {
        ...rating,
        rating_before: player1Before,
        opponent_rating_before: player2Before,
        rating_after: player1After,
        opponent_rating_after: player2After,
      }),
      this.upsert(undefined, {
        ...rating,
        player_id: rating.opponent_id,
        opponent_id: rating.player_id,
        rating_before: player2Before,
        opponent_rating_before: player1Before,
        rating_after: player2After,
        opponent_rating_after: player1After,
      }),
      adminUpdate(rating.player_id, { current_rating: player1After }),
      adminUpdate(rating.opponent_id, { current_rating: player2After }),
    ]);

    // A rebuild running now (or in the last 6 minutes) may have replaced what this save read, or this save
    // may have overwritten the rebuild's current ratings: queue a rebuild of this match if so.
    try {
      await ratingRecalcDueModel.enqueueIfRebuilding(
        rating.rated_at ?? match.played_at ?? new Date().toISOString(),
        [rating.player_id, rating.opponent_id],
      );
    } catch (error) {
      Sentry.captureException(error, {
        tags: { area: 'rating-recalc-due' },
        extra: { matchId: rating.match_id, step: 'enqueue-if-rebuilding' },
      });
    }

    return { player1After, player2After };
  },

  async getRatingByMatchId(matchId: string): Promise<{
    event_type: string;
    event_id: string | null;
    event_name: string | null;
    event_location_name: string | null;
    event_division_name: string | null;
  } | null> {
    const client = ensureClient();

    const { data, error } = await client
      .from('rating')
      .select('event_type, event_id, event_name, event_location_name, event_division_name')
      .eq('match_id', matchId)
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  /**
   * Suppress rating fields for matches belonging to events with ratings off —
   * the event's own switch or its federation's. Replaces myRating, myDelta,
   * oppRating, oppDelta with null (the app's logbook nulls the same matches).
   */
  async suppressRatingsForDisabledFederations(
    matches: MatchWithRating[],
  ): Promise<MatchWithRating[]> {
    const eventIds = [...new Set(matches.map((m) => m.eventId).filter(Boolean))] as string[];
    if (eventIds.length === 0) return matches;

    const admin = getSupabaseAdminClient();
    if (!admin) return matches;

    const { data: events } = await admin
      .from('event')
      .select('id, ratings_enabled, federation:federation_id(ratings_enabled)')
      .in('id', eventIds);

    const disabledEventIds = new Set(
      (events || [])
        .filter((e: any) => e.ratings_enabled === false || e.federation?.ratings_enabled === false)
        .map((e: any) => e.id),
    );

    if (disabledEventIds.size === 0) return matches;

    return matches.map((m) => {
      if (m.eventId && disabledEventIds.has(m.eventId)) {
        return { ...m, myRating: null, myDelta: null, oppRating: null, oppDelta: null };
      }
      return m;
    });
  },
};

export { ratingModel };
export type { MatchWithGames, RatingWithMatch };
